/**
 * RGBA colors
 * @class
 * @param {number} [r=1]
 * @param {number} [g=1]
 * @param {number} [b=1]
 * @param {number} [a=1]
 */
function RGBA(r, g, b, a) {
    this._r = r !== undefined ? r : 1;
    this._g = g !== undefined ? g : 1;
    this._b = b !== undefined ? b : 1;
    this._a = a !== undefined ? a : 1;
}
/**
 * Creates a new RGBA instance
 * @function
 * @param {number} [r=1]
 * @param {number} [g=1]
 * @param {number} [b=1]
 * @param {number} [a=1]
 * @return {RGBA}
 */
RGBA.new = function (r, g, b, a) {
    return new RGBA(r, g, b, a);
};

/**
 * Creates a copy
 * @function
 * @return {RGBA}
 */
RGBA.prototype.copy = function () {
    return RGBA.new(this._r, this._g, this._b, this._a);
};
/**
 * Creates a copy
 * @function
 * @alias RGBA.prototype.copy
 * @return {RGBA}
 */
RGBA.prototype.cp = RGBA.prototype.copy;

/**
 * A string representation of the color. Compatible with canvas styles
 * @return {string}
 */
RGBA.prototype.toString = function (omitAlpha) {
    var s = '';
    if (!omitAlpha) {
        s += 'rgba(';
    } else {
        s += 'rgb(';
    }

    var r = Math.floor(Math.max(0, Math.min(1, this._r)) * 255);
    var g = Math.floor(Math.max(0, Math.min(1, this._g)) * 255);
    var b = Math.floor(Math.max(0, Math.min(1, this._b)) * 255);
    var a = Math.max(0, Math.min(1, this._a));

    s += r + ',';
    s += g + ',';
    s += b;
    if (!omitAlpha) {
        s += ',' + a;
    }

    s += ')';
    return s;
};

/**
 * Creates a svg representation for this color
 * @param {string} [prefix='fill'] Should be either fill or stroke
 * @return {string}
 */
RGBA.prototype.toSVG = function (prefix) {
    var style = '';
    style += prefix + ':' + this.toString(true) + '; ';
    style += prefix + '-opacity:' + this.a() + ';';
    return style;
};

/**
 * Sets or gets the red component
 * @function
 * @param {number} [val]
 * @return {number | RGBA} Returns the value of the red channel, if val is not
 * given, this otherwise
 */
RGBA.prototype.r = function (val) {
    if (val === undefined) {
        return this._r;
    }

    this._r = val;
    return this;
};

/**
 * Sets or gets the green component
 * @function
 * @param {number} [val]
 * @return {number | RGBA} Returns the value of the green channel, if val is not
 * given, this otherwise
 */
RGBA.prototype.g = function (val) {
    if (val === undefined) {
        return this._g;
    }

    this._g = val;
    return this;
};

/**
 * Sets or gets the blue component
 * @function
 * @param {number} [val]
 * @return {number | RGBA} Returns the value of the blue channel, if val is not
 * given, this otherwise
 */
RGBA.prototype.b = function (val) {
    if (val === undefined) {
        return this._b;
    }

    this._b = val;
    return this;
};

/**
 * Sets or gets the alpha component
 * @function
 * @param {number} [val]
 * @return {number | RGBA} Returns the value of the alpha channel, if val is not
 * given, this otherwise
 */
RGBA.prototype.a = function (val) {
    if (val === undefined) {
        return this._a;
    }

    this._a = val;
    return this;
};

function RGBtoHSV({ r, g, b }) {
    var max = Math.max(r, g, b), min = Math.min(r, g, b),
        d = max - min,
        h,
        s = (max === 0 ? 0 : d / max),
        v = max;

    switch (max) {
        case min: h = 0; break;
        case r: h = (g - b) + d * (g < b ? 6 : 0); h /= 6 * d; break;
        case g: h = (b - r) + d * 2; h /= 6 * d; break;
        case b: h = (r - g) + d * 4; h /= 6 * d; break;
        default: break;
    }

    return {
        h: h,
        s: s,
        v: v
    };
}

function HSVtoRGB({ h, s, v }) {
    var r, g, b, i, f, p, q, t;
    i = Math.floor(h * 6);
    f = h * 6 - i;
    p = v * (1 - s);
    q = v * (1 - f * s);
    t = v * (1 - (1 - f) * s);
    switch (i % 6) {
        case 0:
            r = v;
            g = t;
            b = p;
            break;
        case 1: r = q;
            g = v;
            b = p;
            break;
        case 2: r = p;
            g = v;
            b = t;
            break;
        case 3: r = p;
            g = q;
            b = v;
            break;
        case 4: r = t;
            g = p;
            b = v;
            break;
        case 5: r = v;
            g = p;
            b = q;
            break;
        default: break;
    }
    return {
        r, g, b
    };
}

RGBA.prototype.toJSON = function () {
    return {
        r: this._r,
        g: this._g,
        b: this._b,
        a: this._a
    };
};

RGBA.prototype.changeValue = function (percentage = 0.0) {
    let { h, s, v } = RGBtoHSV({ r: this._r, g: this._g, b: this._b });

    v *= (1.0 + percentage);
    v = Math.min(1.0, Math.max(0.0, v));
    const { r, g, b } = HSVtoRGB({ h, s, v });

    return RGBA.new(r, g, b, this._a);
};

/**
 * Red
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.RED = RGBA.new(1, 0, 0, 1);
/**
 * Lime
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.LIME = RGBA.new(0, 1, 0, 1);
/**
 * Blue
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.BLUE = RGBA.new(0, 0, 1, 1);
/**
 * White
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.WHITE = RGBA.new(1, 1, 1, 1);
/**
 * Black
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.BLACK = RGBA.new(0, 0, 0, 1);
/**
 * Yellow
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.YELLOW = RGBA.new(1, 1, 0, 1);
/**
 * Fuchsia
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.FUCHSIA = RGBA.new(1, 0, 1, 1);
/**
 * Aqua
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.AQUA = RGBA.new(0, 1, 1, 1);
/**
 * Green
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.GREEN = RGBA.new(0, 0.5, 0, 1);
/**
 * Purple
 * @constant
 * @default
 */
RGBA.PURPLE = RGBA.new(0.5, 0, 0.5, 1);
/**
 * Orange
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.ORANGE = RGBA.new(1, 0.647, 0, 1);
/**
 * Salmon
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.SALMON = RGBA.new(0.98, 0.5, 0.447, 1);
/**
 * Teal
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.TEAL = RGBA.new(0, 0.5, 0.5, 1);

/**
 * Gray
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.GRAY = RGBA.new(0.5, 0.5, 0.5, 1);
/**
 * Grey
 * @constant
 * @type {RGBA}
 * @alias RGBA.GRAY
 * @default
 */
RGBA.GREY = RGBA.GRAY;
/**
 * Violet
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.VIOLET = RGBA.new(0.93, 0.51, 0.93, 1);

/**
 * Amber
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.AMBER = RGBA.new(1, 0.749, 0, 1);


/**
 * Android green
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.ANDROID_GREEN = RGBA.new(0.643, 0.776, 0.223, 1);

/**
 * Ball blue
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.BALL_BLUE = RGBA.new(0.129, 0.670, 0.803, 1);

/**
 * Bittersweet
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.BITTERSWEET = RGBA.new(0.996, 0.435, 0.368, 1);

/**
 * Brilliant rose
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.BRILLIANT_ROSE = RGBA.new(1, 0.333, 0.639, 1);


/**
 * Celadon
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.CELADON = RGBA.new(0.674, 0.882, 0.686, 1);

/**
 * Ceil
 * @constant
 * @type {RGBA}
 * @default
 */
RGBA.CEIL = RGBA.new(0.572, 0.631, 0.811, 1);

/**
 * List containing default colors
 * @type {Array<RGBA>}
 */
RGBA.DEFAULT_COLORS = [
    RGBA.RED, RGBA.LIME, RGBA.BLUE, RGBA.WHITE,
    RGBA.BLACK, RGBA.YELLOW, RGBA.FUCHSIA, RGBA.AQUA, RGBA.GREEN, RGBA.PURPLE,
    RGBA.ORANGE, RGBA.SALMON, RGBA.TEAL, RGBA.GRAY, RGBA.VIOLET,
    RGBA.AMBER, RGBA.ANDROID_GREEN, RGBA.BALL_BLUE, RGBA.BITTERSWEET,
    RGBA.BRILLIANT_ROSE, RGBA.CELADON, RGBA.CEIL
];

RGBA.getIndexColor = function (i) {
    return RGBA.DEFAULT_COLORS[i % RGBA.DEFAULT_COLORS.length].cp();
};


export {
    RGBA
};
